import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, SectionCard, Chip, EmptyState } from "../components/Common";
import { Building2, Plus, Ban, RotateCcw, X, Sliders } from "lucide-react";
import { toast } from "sonner";

const FEATURE_CATALOG = [
  { key: "dentist",   label: "Dentist",         description: "Adds a Dentist tab to this hospital's app header." },
  { key: "optical",   label: "Optical Centre",  description: "Adds an Optical Centre tab to this hospital's app header." },
  { key: "orthotics", label: "Orthotics Clinic", description: "Adds an Orthotics Clinic tab (braces, prosthetics, gait aids)." },
];

export default function PlatformHospitals() {
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editFeatures, setEditFeatures] = useState(null); // {org}
  const load = async () => {
    try { const { data } = await api.get("/platform/hospitals"); setRows(data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Access denied"); }
  };
  useEffect(() => { load(); }, []);

  const suspend = async (o) => {
    if (!window.confirm(`Suspend ${o.name}? All ${o.user_count} users will be logged out and blocked from signing in.`)) return;
    try { await api.post(`/platform/hospitals/${o.organization_id}/suspend`); toast.success(`${o.name} suspended`); load(); }
    catch { toast.error("Failed"); }
  };
  const reactivate = async (o) => {
    try { await api.post(`/platform/hospitals/${o.organization_id}/reactivate`); toast.success(`${o.name} reactivated`); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="platform-hospitals-page">
      <PageHeader
        title="Platform · Hospitals"
        subtitle="Create and manage hospital accounts. Data isolation is enforced by organization_id on every record."
        actions={<button data-testid="new-hospital-btn" onClick={() => setShowAdd(true)} className="h-10 px-4 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold flex items-center gap-1.5"><Plus className="w-4 h-4" /> Create hospital</button>}
      />

      <SectionCard title={`Hospitals · ${rows.length}`}>
        {rows.length === 0 ? <EmptyState icon={Building2} title="No hospitals yet" description="Create your first hospital via the button above." /> : (
          <table className="w-full text-sm" data-testid="hospitals-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Location</th>
                <th className="text-left px-4 py-2.5">Features</th>
                <th className="text-right px-4 py-2.5">Users</th>
                <th className="text-right px-4 py-2.5">Patients</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((o) => {
                const enabled = FEATURE_CATALOG.filter((f) => o.features?.[f.key]);
                return (
                <tr key={o.organization_id} data-testid={`hospital-${o.organization_id}`}>
                  <td className="px-4 py-2.5 font-medium">{o.name}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{[o.district, o.country].filter(Boolean).join(" · ")}</td>
                  <td className="px-4 py-2.5">
                    {enabled.length === 0 ? <span className="text-xs text-slate-400">—</span> : (
                      <div className="flex flex-wrap gap-1">
                        {enabled.map((f) => <Chip key={f.key} tone="blue">{f.label}</Chip>)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular">{o.user_count}</td>
                  <td className="px-4 py-2.5 text-right tabular">{o.patient_count}</td>
                  <td className="px-4 py-2.5"><Chip tone={o.status === "active" ? "green" : "red"}>{o.status}</Chip></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`features-${o.organization_id}`} onClick={() => setEditFeatures(o)} className="text-xs text-[#1565C0] font-medium hover:underline inline-flex items-center gap-1 mr-3"><Sliders className="w-3 h-3" /> Features</button>
                    {o.status === "active" ? (
                      <button data-testid={`suspend-${o.organization_id}`} onClick={() => suspend(o)} className="text-xs text-[#B71C1C] font-medium hover:underline inline-flex items-center gap-1"><Ban className="w-3 h-3" /> Suspend</button>
                    ) : (
                      <button data-testid={`reactivate-${o.organization_id}`} onClick={() => reactivate(o)} className="text-xs text-emerald-700 font-medium hover:underline inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Reactivate</button>
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </SectionCard>

      {showAdd && <AddHospitalModal onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />}
      {editFeatures && <FeaturesModal org={editFeatures} onClose={() => setEditFeatures(null)} onDone={() => { setEditFeatures(null); load(); }} />}
    </div>
  );
}

function FeaturesModal({ org, onClose, onDone }) {
  const [selected, setSelected] = useState(() => {
    const init = {};
    FEATURE_CATALOG.forEach((f) => { init[f.key] = !!org.features?.[f.key]; });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/platform/hospitals/${org.organization_id}/features`, { features: selected });
      const on = Object.keys(selected).filter((k) => selected[k]).length;
      toast.success(`${org.name} · ${on} feature${on === 1 ? "" : "s"} enabled. Hospital staff sign-in / refresh to see the new tabs.`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save features"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="features-modal">
        <div className="flex items-center justify-between mb-2"><div><div className="font-semibold">Manage features</div><div className="text-xs text-slate-500">{org.name}</div></div><button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button></div>
        <div className="text-xs text-slate-500 mb-4">Enable optional modules that appear as tabs in this hospital's app header. Each feature is fully isolated — enabling it here does not affect any other hospital.</div>
        <div className="space-y-2">
          {FEATURE_CATALOG.map((f) => (
            <label key={f.key} data-testid={`feature-toggle-${f.key}`} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" checked={!!selected[f.key]} onChange={(e) => setSelected((s) => ({ ...s, [f.key]: e.target.checked }))} className="mt-0.5 w-4 h-4 accent-[#1976D2]" />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-800">{f.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{f.description}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button data-testid="features-submit" disabled={saving} onClick={submit} className="h-10 px-5 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold disabled:opacity-50">{saving ? "Saving…" : "Save features"}</button>
        </div>
      </div>
    </div>
  );
}

function AddHospitalModal({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", country: "Uganda", district: "", owner_name: "", owner_email: "", owner_password: "" });
  const submit = async () => {
    if (!f.name.trim() || !f.owner_name.trim() || !f.owner_email.trim()) return toast.error("Hospital name, owner name and owner email are required");
    if ((f.owner_password || "").length < 8) return toast.error("Owner password must be at least 8 characters");
    try {
      const { data } = await api.post("/platform/hospitals", f);
      toast.success(`${data.name} created · owner ${data.owner.email}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="add-hospital-modal">
        <div className="flex items-center justify-between mb-4"><div className="font-semibold">Create hospital</div><button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button></div>
        <div className="space-y-3">
          {[["name","Hospital name *"],["country","Country"],["district","District / city"]].map(([k, l]) => (
            <label key={k} className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">{l}</span>
              <input data-testid={`hosp-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </label>
          ))}
          <div className="text-[11px] uppercase text-slate-500 font-semibold pt-2">First user · Hospital Owner</div>
          {[["owner_name","Owner name *"],["owner_email","Owner email *"],["owner_password","Temp password (≥ 8 chars) *"]].map(([k, l]) => (
            <label key={k} className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">{l}</span>
              <input data-testid={`hosp-${k}`} type={k === "owner_password" ? "password" : "text"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </label>
          ))}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button data-testid="hosp-submit" onClick={submit} className="h-10 px-5 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold">Create & assign owner</button>
        </div>
      </div>
    </div>
  );
}
